import { useEffect, useState } from 'react';
import i18n from 'i18next';

const LANGUAGE_STORAGE_KEY = 'i18nextLng';

/**
 * Get the current language from localStorage or return default
 */
export const getStoredLanguage = (): string => {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return stored && ['en', 'de'].includes(stored) ? stored : 'de';
  } catch {
    return 'de';
  }
};

/**
 * Set language in localStorage and update i18n
 */
export const setLanguage = (language: 'en' | 'de'): void => {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    i18n.changeLanguage(language);
  } catch {
    // Still change the language even if localStorage fails
    i18n.changeLanguage(language);
  }
};

/**
 * Initialize language from localStorage on app startup
 */
export const initializeLanguage = (): void => {
  const storedLanguage = getStoredLanguage();
  if (storedLanguage !== i18n.language) {
    i18n.changeLanguage(storedLanguage);
  }
};

/**
 * Hook to get current language and setter function
 */
export const useLanguage = () => {
  const [currentLanguage, setCurrentLanguage] = useState<'en' | 'de'>(i18n.language as 'en' | 'de');

  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      setCurrentLanguage(lng as 'en' | 'de');
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, []);

  const changeLanguage = (language: 'en' | 'de') => {
    setLanguage(language);
  };

  return { currentLanguage, changeLanguage };
};

/**
 * Get the display name for a language code
 */
export const getLanguageDisplayName = (language: 'en' | 'de'): string => {
  return language === 'en' ? 'English' : 'Deutsch';
};

/**
 * Get the flag emoji for a language code
 */
export const getLanguageFlag = (language: 'en' | 'de'): string => {
  return language === 'en' ? '🇺🇸' : '🇩🇪';
};
